/** Logical portrait canvas; the root container is scaled to fit the real screen. */
export const L = {
  W: 360,
  H: 720,
  hudY: 0,
  hudH: 44,
  /** Board: 6 x 8 cells, row 0 is the enemy edge, row 7 touches the bunker. */
  gridY: 50,
  cell: 54,
  gap: 2,
  /** Info strip under the board: wave preview or selected building. */
  stripY: 500,
  stripH: 58,
  wallY: 562,
  wallH: 14,
  /** Shop row: three shop slots, the reroll square, then the three pocket slots (5 October). */
  shopY: 584,
  shopCell: 48,
  shopGap: 6,
  rerollW: 44,
  pocketCell: 38,
  buttonsY: 664,
  buttonH: 46,
} as const;

/** Cartoon palette in the spirit of the reference: water around a green island, wooden UI. */
export const COLORS = {
  water: 0x2f8fd6,
  waterDeep: 0x2274ad,
  island: 0x6cae4f,
  islandEdge: 0x3f7a35,
  grassA: 0x7cc25a,
  grassB: 0x72b854,
  grassLine: 0x5a9a40,
  /** Grass gradient: the far (enemy) edge and the near (bunker) edge of the board. */
  grassFar: 0x5fa64a,
  grassNear: 0x8fd268,
  dirt: 0xd8b479,
  dirtLine: 0xa8865a,
  dirtLight: 0xe8ca92,
  rock: 0x9aa3ad,
  rockDark: 0x5f6873,
  rockLight: 0xd3d9df,
  ink: 0x1f2430,
  panel: 0x1b1e26,
  panelLine: 0x2f3542,
  wood: 0x6b4a2e,
  woodLight: 0x8a6239,
  text: 0xf3f4f6,
  textDim: 0xa6adbd,
  gold: 0xf5c451,
  hp: 0x4caf50,
  hpBg: 0x2b2f38,
  danger: 0xff5e57,
  accent: 0x4fa3ff,
  button: 0x2d6cdf,
  buttonDisabled: 0x3a3f4b,
  star: 0xff4d4d,
} as const;

export const RARITY_COLORS = { common: 0xa6adbd, rare: 0x4fa3ff, epic: 0xb478ff } as const;

/** Board palette a field skin swaps in; everything else on screen keeps the base colours. */
export interface FieldPalette {
  water: number;
  waterDeep: number;
  shore: number;
  island: number;
  grassFar: number;
  grassNear: number;
  dirt: number;
  dirtLight: number;
}

export const FIELD_PALETTES: Record<string, FieldPalette> = {
  default: { water: COLORS.water, waterDeep: COLORS.waterDeep, shore: 0xe6cf9a, island: COLORS.island, grassFar: COLORS.grassFar, grassNear: COLORS.grassNear, dirt: COLORS.dirt, dirtLight: COLORS.dirtLight },
  night: { water: 0x1b2b4a, waterDeep: 0x14213a, shore: 0x8b7d63, island: 0x3b6b3f, grassFar: 0x2f5a35, grassNear: 0x4f8a4f, dirt: 0x9c8663, dirtLight: 0xb59d78 },
  winter: { water: 0x6fb1d9, waterDeep: 0x4f93c0, shore: 0xf1f4f7, island: 0xd9e6ee, grassFar: 0xc7d9e4, grassNear: 0xf0f6fa, dirt: 0xb9a48b, dirtLight: 0xd3c2ad },
  desert: { water: 0x3aa0c8, waterDeep: 0x2a84ad, shore: 0xf0d9a3, island: 0xd9b76e, grassFar: 0xc9a25e, grassNear: 0xe8c987, dirt: 0xa77b4a, dirtLight: 0xc2965f },
};

/**
 * The stage is not clipped to the logical canvas, so backgrounds overdraw by this much on every side
 * and fill the letterbox bands on screens taller or wider than 1:2 instead of leaving them black.
 */
export const BLEED = 260;

export function hexToNum(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}
